import { cn } from "@/lib/utils";

type WordmarkSize = "sm" | "md" | "lg" | "xl";
type WordmarkOrientation = "inline" | "stacked";

// `xl` is the hero size. It uses a clamp() inline style instead of Tailwind
// responsive utilities because the prior `text-6xl sm:text-7xl md:text-8xl`
// ramp (60→72→96px) overflowed the available content width at viewports
// below 640px — at 320px / 375px the 60px wordmark plus letter-spacing
// runs wider than the `px-6` parent's content area. clamp(40px, 10vw, 96px)
// scales fluidly between mobile and desktop while preserving the desktop
// visual (96px == `text-8xl`).
const XL_FONT_SIZE = "clamp(2.5rem, 10vw, 6rem)";

const sizeToClass: Record<Exclude<WordmarkSize, "xl">, string> = {
  sm: "text-lg sm:text-xl",
  md: "text-2xl sm:text-3xl",
  lg: "text-4xl sm:text-5xl",
};

type WordmarkProps = {
  size?: WordmarkSize;
  orientation?: WordmarkOrientation;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
};

export function Wordmark({
  size = "md",
  orientation = "inline",
  as: Component = "span",
  className,
}: WordmarkProps) {
  const stacked = orientation === "stacked";
  return (
    <Component
      className={cn(
        "font-serif font-normal not-italic",
        size === "xl" ? undefined : sizeToClass[size],
        stacked ? "inline-block whitespace-pre-line" : "whitespace-nowrap",
        className,
      )}
      style={{
        letterSpacing: "-0.005em",
        lineHeight: stacked ? 0.88 : 0.92,
        ...(size === "xl" ? { fontSize: XL_FONT_SIZE } : {}),
      }}
    >
      {stacked ? "Sand Point\nRoad" : "Sand Point Road"}
    </Component>
  );
}
